
import { portfolioSlug } from "@/lib/public-content";
import React, { useState, useRef } from 'react';
import { PortfolioItem } from '@/hooks/useProfile';

interface GalleryItemProps {
  item: PortfolioItem;
  itemId: string;
  imageUrl: string | null;
  hasMultipleImages: boolean;
  imageCount: number;
  imageLoadStatus?: Record<string, 'loading' | 'success' | 'error'>;
  aspectRatioClass?: string;
  yearText?: string;
  usernameSlug?: string;
  onOpenModal?: (item: PortfolioItem) => void;
  shouldShowBlur?: boolean;
}

const GalleryItem: React.FC<GalleryItemProps> = ({
  item,
  itemId,
  imageUrl,
  hasMultipleImages,
  imageCount,
  imageLoadStatus = {},
  aspectRatioClass = 'aspect-video',
  yearText,
  usernameSlug,
  onOpenModal,
  shouldShowBlur = false
}) => {
  const [retryAttempts, setRetryAttempts] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle image load error with retry
  const handleImageError = () => {
    if (retryAttempts < 2) {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        setRetryAttempts(prev => prev + 1);
      }, 1000 * (retryAttempts + 1));
    }
  };

  React.useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const loadStatus = imageLoadStatus[`${itemId}-0`] || 'loading';
  const isLoading = loadStatus === 'loading';
  const hasError = loadStatus === 'error';

  const handleClick = () => {
    if (onOpenModal) {
      onOpenModal(item);
    }
  };

  return (
    <div className="flex flex-col cursor-pointer w-full mb-2 h-full">
      <div 
        className="flex flex-col overflow-hidden bg-white rounded-lg border shadow-sm dark:border-gray-800 hover:shadow-md transition-all duration-300 hover:-translate-y-1 h-full"
      >
        <div 
          className="relative overflow-hidden   rounded-t-lg" 
          onClick={handleClick}
        >
          <div
  className={`${aspectRatioClass} relative flex items-center justify-center overflow-hidden ${!shouldShowBlur ? 'bg-muted' : ''}`}
  style={imageUrl && shouldShowBlur ? { backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
>
  {imageUrl && (
    <img
      src={`${imageUrl}${retryAttempts > 0 ? `?retry=${retryAttempts}` : ''}`}
      alt={item.title || 'Portfolio Item'}
      className={`relative z-10 ${shouldShowBlur ? 'w-[65%] h-auto object-contain rounded-lg shadow-[0_0_25px_rgba(0,0,0,0.08),0_0_50px_rgba(0,0,0,0.05)]' : 'w-full h-full object-cover'}`}
      loading="lazy"
    />
  )}
  {hasError && (
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      …
    </div>
  )}
  {/* optional blur/tint layer */}
  {imageUrl && shouldShowBlur && <div className="absolute inset-0 bg-white/40 backdrop-blur" aria-hidden="true" />}
</div>

        </div>
        <div className="px-3 py-2 bg-white border-t border-gray-100 rounded-b-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-medium line-clamp-1 text-ellipsis"><a href={`/u/${encodeURIComponent(usernameSlug || '')}/portfolio/${encodeURIComponent(portfolioSlug(item))}`} onClick={event => { if (onOpenModal && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onOpenModal(item); } }}>{item.title || 'Untitled Project'}</a></h3>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-amber-500"></div>
                  <span className="text-xs text-muted-foreground">Shot</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryItem;
